import { useFlag } from '@reflag/react-sdk';
import type * as React from 'react';
import * as z from 'zod';

import { AlertBanner } from '@gitbook/spine-react';

const AppBannerConfigSchema = z
    .object({
        title: z.string().min(1).optional(),
        message: z.string().min(1).optional(),
        link: z.string().optional(),
        kind: z
            .enum(['default', 'info', 'danger', 'success', 'merge', 'upgrade', 'warning'])
            .default('warning'),
    })
    .refine((data) => data.title || data.message, {
        message: 'Either title or message must be provided',
        path: ['title', 'message'],
    });

/**
 * Remote-controlled app banner component using Bucket remote config
 * Used to display important messages or announcements like service interruptions, etc.
 */
export function AppBanner(): React.ReactElement | null {
    const { isEnabled, config } = useFlag('APP_BANNER');

    if (!isEnabled || !('key' in config) || !config.key) {
        return null;
    }

    const parseResult = AppBannerConfigSchema.safeParse(config.payload);

    if (!parseResult.success) {
        // invalid config, do not render the banner
        return null;
    }

    const { title, message, kind, link } = parseResult.data;

    return (
        <AlertBanner
            kind={kind}
            title={title || undefined}
            details={message || undefined}
            action={
                link
                    ? {
                          label: 'View Status',
                          href: link,
                          target: '_blank',
                          kind: 'link',
                      }
                    : undefined
            }
        />
    );
}
